import React, { useEffect, useMemo, useState } from 'react';
import { Table, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { KTSVG } from '../../_metronic/helpers';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import Spinner from 'react-bootstrap/Spinner';
import { getThisFiscalYearRange } from '../helpers/GlobalHelper';

interface PlannerUser {
    id: number;
    name: string;
}

interface PlannerMonth {
    year: number;
    month: number;
    label: string;
    key: string;
}

interface TargetCell {
    idTargets: number | null;
    target: string;
}

interface PlannerData {
    fiscalYear: string;
    users: PlannerUser[];
    months: PlannerMonth[];
    targets: {
        [userId: string]: {
            [monthKey: string]: {
                idTargets: number;
                target: number;
            };
        };
    };
}

const stickyHeaderStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 1022,
    backgroundColor: '#f8f9fa',
    boxShadow: '0 2px 2px -1px rgba(0, 0, 0, 0.4)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
};

const stickyFirstColumnStyle: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 1021,
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
};

const getCurrentFiscalYear = () => {
    const { fromDate } = getThisFiscalYearRange();
    const startYear = parseInt(fromDate.slice(0, 4), 10);
    return `${startYear}-${startYear + 1}`;
};

const getFiscalYearOptions = () => {
    const { fromDate } = getThisFiscalYearRange();
    const currentStartYear = parseInt(fromDate.slice(0, 4), 10);
    const options = [];

    for (let offset = -2; offset <= 3; offset += 1) {
        const startYear = currentStartYear + offset;
        options.push({
            label: `${startYear}-${startYear + 1}`,
            value: `${startYear}-${startYear + 1}`,
        });
    }

    return options;
};

const buildEmptyTargets = (users: PlannerUser[], months: PlannerMonth[], loadedTargets: PlannerData['targets']) => {
    const nextTargets: Record<string, Record<string, TargetCell>> = {};

    users.forEach((user) => {
        const userKey = String(user.id);
        nextTargets[userKey] = {};

        months.forEach((month) => {
            const existing = loadedTargets[userKey]?.[month.key];
            nextTargets[userKey][month.key] = {
                idTargets: existing?.idTargets ?? null,
                target: existing?.target !== undefined && existing?.target !== null
                    ? String(existing.target)
                    : '',
            };
        });
    });

    return nextTargets;
};

const TargetsPlanner: React.FC = () => {
    const fiscalYearOptions = useMemo(() => getFiscalYearOptions(), []);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState<{ label: string; value: string } | null>(null);
    const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());
    const [plannerData, setPlannerData] = useState<PlannerData | null>(null);
    const [targets, setTargets] = useState<Record<string, Record<string, TargetCell>>>({});

    useEffect(() => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                }));
                setProjects(projectOptions);
                if (projectOptions.length > 0) {
                    setProject(projectOptions[0]);
                }
            })
            .catch((error) => console.error('Error fetching projects:', error));
    }, []);

    const loadPlanner = async () => {
        if (!project?.value) {
            toastError('Please select a project.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}targets-planner?idProjects=${project.value}&fiscalYear=${encodeURIComponent(fiscalYear)}`,
                getRequestOptions()
            );
            const data = await response.json();

            if (!data.success) {
                setPlannerData(null);
                setTargets({});
                toastError(data.message ?? 'Failed to load targets planner.');
                return;
            }

            const loadedData = data.data as PlannerData;
            setPlannerData(loadedData);
            setTargets(buildEmptyTargets(loadedData.users, loadedData.months, loadedData.targets));
        } catch (error) {
            console.error('Error loading targets planner:', error);
            toastError('Failed to load targets planner.');
        } finally {
            setLoading(false);
        }
    };

    const toastError = (message: string) => {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: message,
        });
    };

    const handleTargetChange = (userId: number, monthKey: string, value: string) => {
        if (!plannerData) {
            return;
        }

        const userKey = String(userId);
        const monthIndex = plannerData.months.findIndex((month) => month.key === monthKey);

        setTargets((prev) => {
            const updatedUserRow = { ...prev[userKey] };

            updatedUserRow[monthKey] = {
                ...updatedUserRow[monthKey],
                target: value,
            };

            if (value !== '') {
                for (let i = monthIndex + 1; i < plannerData.months.length; i += 1) {
                    const rightMonthKey = plannerData.months[i].key;
                    updatedUserRow[rightMonthKey] = {
                        ...updatedUserRow[rightMonthKey],
                        target: value,
                    };
                }
            }

            return {
                ...prev,
                [userKey]: updatedUserRow,
            };
        });
    };

    const handleSave = async () => {
        if (!project?.value || !plannerData) {
            toastError('Please load the planner before saving.');
            return;
        }

        const payloadTargets: Array<{
            idSr: number;
            year: number;
            month: number;
            target: number | string;
            idTargets: number | null;
        }> = [];

        plannerData.users.forEach((user) => {
            const userKey = String(user.id);
            plannerData.months.forEach((month) => {
                const cell = targets[userKey]?.[month.key];
                payloadTargets.push({
                    idSr: user.id,
                    year: month.year,
                    month: month.month,
                    target: cell?.target ?? '',
                    idTargets: cell?.idTargets ?? null,
                });
            });
        });

        setSaving(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}targets-planner/save`,
                postRequestOptions({
                    idProjects: Number(project.value),
                    fiscalYear,
                    targets: payloadTargets,
                })
            );
            const data = await response.json();

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved',
                    text: data.successMessage ?? 'Targets have been saved.',
                });
                loadPlanner();
            } else {
                toastError(data.message ?? 'Failed to save targets.');
            }
        } catch (error) {
            console.error('Error saving targets planner:', error);
            toastError('Failed to save targets.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className='card card-custom'>
            <div className='card-header'>
                <div className="card-title">
                    <KTSVG path='/media/svg/icons/Text/Bullet-list.svg' className='svg-icon-1 svg-icon-primary' />
                    <span className='text fw-bolder' style={{ marginLeft: '10px' }}>
                        Targets Planner
                    </span>
                </div>
            </div>

            <div className='card-body'>
                <div className='row mb-4'>
                    <div className='col-md-4'>
                        <Form.Group controlId="project">
                            <Form.Label>Project<span className='required'></span></Form.Label>
                            <Select
                                value={project}
                                onChange={(selected) => setProject(selected)}
                                options={projects}
                                menuPosition="fixed"
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                isClearable={false}
                            />
                        </Form.Group>
                    </div>
                    <div className='col-md-3'>
                        <Form.Group controlId="fiscalYear">
                            <Form.Label>Year<span className='required'></span></Form.Label>
                            <Form.Select
                                value={fiscalYear}
                                onChange={(event) => setFiscalYear(event.target.value)}
                            >
                                {fiscalYearOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className='col-md-3 d-flex align-items-end'>
                        <Button variant="primary" size="sm" onClick={loadPlanner} disabled={loading || !project?.value}>
                            {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                            Load Planner
                        </Button>
                    </div>
                </div>

                {plannerData ? (
                    <>
                        <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <strong>Period:</strong> {plannerData.months[0]?.label} - {plannerData.months[plannerData.months.length - 1]?.label}
                            </div>
                            <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
                                {saving && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                                Save Targets
                            </Button>
                        </div>

                        <div style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
                            <Table bordered hover size="sm" style={{ marginBottom: 0, minWidth: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...stickyHeaderStyle, ...stickyFirstColumnStyle, left: 0, zIndex: 1023 }}>
                                            Employee
                                        </th>
                                        {plannerData.months.map((month) => (
                                            <th key={month.key} className="text-end" style={stickyHeaderStyle}>
                                                {month.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {plannerData.users.length > 0 ? (
                                        plannerData.users.map((user) => (
                                            <tr key={user.id}>
                                                <td style={stickyFirstColumnStyle}>{user.name}</td>
                                                {plannerData.months.map((month) => {
                                                    const cell = targets[String(user.id)]?.[month.key];
                                                    return (
                                                        <td key={month.key} className="p-1">
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                min="0"
                                                                step="0.01"
                                                                className="text-end"
                                                                value={cell?.target ?? ''}
                                                                onChange={(event) => handleTargetChange(user.id, month.key, event.target.value)}
                                                                placeholder="0"
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={plannerData.months.length + 1} className="text-center">
                                                No eligible employees found for this project.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                ) : (
                    <div className="text-muted">Select a project and year, then click Load Planner.</div>
                )}
            </div>
        </div>
    );
};

export default TargetsPlanner;
